import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import FormContainer from '@components/FormContainer';
import InputFieldControlled from '@components/InputFieldControlled';
import InputMultiSelect from '@components/InputMultiSelect';
import InputPhone from '@components/InputPhone';
import InputPostal from '@components/InputPostal';
import InputSwitchControlled from '@components/InputSwitch/InputSwitchControlled';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { GET_PRO, UPDATE_PRO_ADMINISTRATIVE_PRO } from '@gql/pros';
import { useOptionsContext } from '@modules/talents/context/options-context';
import { Grid } from '@mui/material';
import { isString } from 'lodash';
import { useForm } from 'react-hook-form';

type Props = {
    pro?: any;
    onSuccess: (data?: any) => void;
    onError: (error?: any) => void;
    onBack?: () => void;
    isEdit?: boolean;
    disabled?: boolean;
};

const AdministrativeForm = React.forwardRef((props: Props, ref) => {
    const { pro, onSuccess, onError, onBack, isEdit, disabled } = props;
    const [loading, setLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [formState, setFormState] = useState<'view' | 'edit'>('view');
    const [hide, setHide] = useState(false);
    const handleHide = (hideState: boolean) => {
        setHide(hideState);
    };

    const defaultValues = useMemo(
        () => ({
            paramNextStep: '',
            name: pro?.name,
            commercialName: pro?.commercialName,
            siret: pro?.siret,
            rcs: pro?.rcs,
            rm: pro?.rm,
            vat: pro?.vat,
            capital: pro?.capital,
            auto: pro?.auto,
            legal: pro?.legal,
            ape: pro?.ape,
            address: pro?.address,
            address2: pro?.address2,
            city: pro?.city,
            country: pro?.country,
            postal: pro?.postal,
            sector: isString(pro?.sector) ? pro?.sector : pro?.sector?._id,
            phoneNumber: pro?.phoneNumber,
            experience: pro?.experience,
            social: {
                instagram: pro?.social?.instagram,
                linkedin: pro?.social?.linkedin,
                website: pro?.social?.website,
            },
        }),
        [pro],
    );

    const {
        control,
        handleSubmit,
        trigger,
        setValue,
        register,
        reset,
        formState: { isDirty },
    } = useForm({
        mode: 'all',
        defaultValues,
    });

    const [update] = useMutation(UPDATE_PRO_ADMINISTRATIVE_PRO, {
        onCompleted: () => {
            enqueueSnackbar('Informations modifiées avec succès', {
                variant: 'success',
            });
        },
        onError: error => {
            enqueueSnackbar(error.message, {
                variant: 'error',
            });

            props.onError(error);
            setLoading(false);
        },
    });

    const submitHandler = useCallback(
        async (data: any) => {
            if (loading) return;
            setLoading(true);
            const paramNextStep: any = data?.paramNextStep;
            delete data.paramNextStep;
            const result = await update({
                variables: {
                    proInput: {
                        ...data,
                        phoneNumber: data.phoneNumber,
                        auto: !!data.auto,
                    },
                    proId: props.pro?._id,
                },
                refetchQueries: () => [
                    {
                        query: GET_PRO,
                        variables: { proId: props.pro?._id },
                    },
                ],
            });
            setLoading(false);
            setHide(false);

            if (!!result.errors) {
                onError(result.errors);
                return;
            }
            data.paramNextStep = paramNextStep;
            onSuccess(data);
            setFormState('view');
        },
        [loading, update, onSuccess, props.pro?._id, onError],
    );
    // register trigger so it can triggered by parent component
    React.useImperativeHandle(ref, () => ({
        submit: async () => {
            const isValid = await trigger();
            if (isValid && isDirty) {
                await handleSubmit(submitHandler)();
            }
            return isValid;
        },
    }));


    const { data: optionsData } = useOptionsContext();

    const sectorOptions = useMemo(
        () => optionsData?.options?.sectors?.[0]?.children?.map(item => ({ label: item.name, value: item.id })) || [],
        [optionsData],
    );

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues, reset]);

    return (
        <FormContainer
            onBack={onBack}
            loading={loading}
            formState={formState}
            setFormState={setFormState}
            onCancel={() => {
                setHide(false);
                reset();
            }}
            hideEdit={disabled}
            onSubmit={handleSubmit(submitHandler)}
            submitText={isEdit ? 'Enregistrer' : 'Suivant'}
            formTitle="Votre société"
            formDescription="Veuillez vérifier les informations de votre société."
            breadcrumbs={['Mes informations', 'Votre société']}>

            <input name="experience" type="hidden" {...register('experience')} />
            <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} md={6}>
                    <InputFieldControlled
                        viewOnly={formState === 'view'}
                        // disabled
                        name="siret"
                        control={control}
                        label="N° Siret"
                        placeholder="Numéro de SIRET"
                        defaultValue={defaultValues?.siret}
                        required
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputFieldControlled
                        viewOnly={formState === 'view'}
                        // disabled
                        name="rcs"
                        control={control}
                        label="RCS de"
                        placeholder="RCS de"
                        defaultValue={defaultValues?.rcs}
                        required
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputFieldControlled
                        viewOnly={formState === 'view'}
                        // disabled
                        name="rm"
                        control={control}
                        label="Répertoire des métiers"
                        placeholder="Numéro"
                        defaultValue={defaultValues?.rm}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputFieldControlled
                        viewOnly={formState === 'view'}
                        // disabled
                        label="Raison sociale"
                        name="name"
                        control={control}
                        placeholder="Raison sociale"
                        defaultValue={defaultValues?.name}
                        required
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputFieldControlled
                        viewOnly={formState === 'view'}
                        // disabled
                        name="legal"
                        control={control}
                        label="Forme juridique"
                        placeholder="Forme juridique"
                        defaultValue={defaultValues?.legal}
                        required
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputFieldControlled
                        viewOnly={formState === 'view'}
                        name="commercialName"
                        control={control}
                        label="Nom commercial"
                        placeholder="Nom commercial"
                        defaultValue={defaultValues?.commercialName}
                        required
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputFieldControlled
                        viewOnly={formState === 'view'}
                        // disabled
                        name="capital"
                        control={control}
                        label="Capital Social"
                        placeholder="Numéro"
                        defaultValue={defaultValues?.capital}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputFieldControlled
                        viewOnly={formState === 'view'}
                        // disabled
                        name="ape"
                        control={control}
                        label="Code APE (NAF)"
                        placeholder="Code APE (NAF)"
                        defaultValue={defaultValues?.ape}
                        required
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputSwitchControlled
                        viewOnly={formState === 'view'}
                        name="auto"
                        control={control}
                        label="TVA Applicable"
                        defaultValue={defaultValues?.auto}
                        rules={{
                            validate: val => {
                                return val !== undefined || 'error';
                            },
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputFieldControlled
                        viewOnly={formState === 'view'}
                        // disabled
                        name="vat"
                        control={control}
                        label="Numéro TVA intracommunautaire"
                        defaultValue={defaultValues?.vat}
                        placeholder="Numéro"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputMultiSelect
                        viewOnly={formState === 'view'}
                        single
                        control={control}
                        name="sector"
                        label="Secteur d'activité"
                        placeholder="Choisissez votre secteur"
                        options={sectorOptions}
                        defaultValue={defaultValues?.sector}
                        required
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputFieldControlled
                        viewOnly={formState === 'view'}
                        // disabled
                        name="address"
                        control={control}
                        label="Adresse professionnelle"
                        placeholder="Adresse professionnelle"
                        defaultValue={defaultValues?.address}
                        required
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputFieldControlled
                        viewOnly={formState === 'view'}
                        name="address2"
                        control={control}
                        label="Adresse complémentaire"
                        placeholder="Adresse complémentaire"
                        defaultValue={defaultValues?.address2}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputPostal
                        viewOnly={formState === 'view'}
                        // disabled
                        name="postal"
                        required
                        defaultValue={defaultValues?.postal}
                        control={control}
                        label="Code postal"
                        placeholder="Code postal"
                        onSelect={(city: string) => {
                            setValue('city', city);
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputFieldControlled
                        viewOnly={formState === 'view'}
                        // disabled
                        name="city"
                        label="Ville"
                        placeholder="Ville"
                        control={control}
                        defaultValue={defaultValues?.city}
                        required
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputFieldControlled
                        viewOnly={formState === 'view'}
                        // disabled
                        name="country"
                        label="Pays"
                        placeholder="Pays"
                        control={control}
                        defaultValue={defaultValues?.country}
                        required
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputPhone
                        viewOnly={formState === 'view'}
                        defaultValue={defaultValues?.phoneNumber}
                        label="Télephone professionnel"
                        name="phoneNumber"
                        control={control}
                        hide={hide}
                        handleHide={handleHide}
                        placeholder="N° de téléphone"
                        required
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputFieldControlled
                        viewOnly={formState === 'view'}
                        name="social.website"
                        optional
                        control={control}
                        label="Adresse de votre site internet"
                        placeholder="Link de votre site ou profil. Ex: https://www.mon-site.com"
                    />
                </Grid>
            </Grid>
        </FormContainer>
    );
});

export default AdministrativeForm;
